import { useState } from 'react';
import { PencilSimple, Plus } from '@phosphor-icons/react';
import type { Course, Program } from '@cvs/shared';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { Button, Card, ErrorState, LoadingState, Modal, Pill, SectionHeading } from '../components/ui';

/**
 * Studio → Courses. Manage programs (groups of courses) and courses. A course's
 * curriculum is built in Content, and learners are enrolled in Learners & Access.
 */
export function StudioCourses() {
  const s = useAsync(
    () => Promise.all([api.admin.courses(), api.admin.programs()]).then(([courses, programs]) => ({ courses, programs })),
    [],
  );
  const [err, setErr] = useState<string | null>(null);

  if (s.loading) return <LoadingState />;
  if (s.error || !s.data) return <ErrorState message={s.error ?? 'Failed to load'} onRetry={s.reload} />;

  const { courses, programs } = s.data;

  return (
    <div className="space-y-6">
      <SectionHeading
        eyebrow="Studio"
        title="Courses"
        description="Group courses into programs, then create and manage courses. Build curriculum in Content and enroll learners in Learners & Access."
      />
      {err && <ErrorState message={err} onRetry={() => setErr(null)} />}
      <ProgramsPanel programs={programs} onChanged={s.reload} onError={setErr} />
      <CoursesPanel courses={courses} programs={programs} onChanged={s.reload} onError={setErr} />
    </div>
  );
}

// ── Programs: create / rename ─────────────────────────────────────────────────
function ProgramsPanel({
  programs,
  onChanged,
  onError,
}: {
  programs: Program[];
  onChanged: () => void;
  onError: (msg: string) => void;
}) {
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  async function create() {
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    try {
      await api.admin.createProgram({ name });
      setNewName('');
      onChanged();
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Could not create program');
    } finally {
      setCreating(false);
    }
  }

  async function rename(p: Program) {
    const name = editName.trim();
    if (!name || name === p.name) {
      setEditId(null);
      return;
    }
    try {
      await api.admin.updateProgram(p.id, { name });
      setEditId(null);
      onChanged();
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Could not rename program');
    }
  }

  return (
    <Card className="space-y-4 p-5">
      <h3 className="font-semibold text-brand-navy">
        Programs <span className="text-sm font-normal text-slate-400">— group related courses</span>
      </h3>
      {programs.length > 0 && (
        <ul className="divide-y divide-slate-100">
          {programs.map((p) => (
            <li key={p.id} className="flex items-center gap-3 py-2.5">
              {editId === p.id ? (
                <input
                  autoFocus
                  className="input w-full max-w-sm"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') rename(p);
                    if (e.key === 'Escape') setEditId(null);
                  }}
                  onBlur={() => rename(p)}
                />
              ) : (
                <button
                  className="flex-1 text-left font-medium text-brand-navy hover:text-brand-teal"
                  title="Rename program"
                  onClick={() => {
                    setEditId(p.id);
                    setEditName(p.name);
                  }}
                >
                  {p.name}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      <div className="flex items-center gap-2">
        <input
          className="input w-full max-w-sm"
          placeholder="New program name…"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') create();
          }}
        />
        <Button icon={Plus} onClick={create} loading={creating} disabled={!newName.trim()}>
          Add program
        </Button>
      </div>
    </Card>
  );
}

// ── Courses list + new/edit modal ────────────────────────────────────────────
function CoursesPanel({
  courses,
  programs,
  onChanged,
  onError,
}: {
  courses: Course[];
  programs: Program[];
  onChanged: () => void;
  onError: (msg: string) => void;
}) {
  const [modal, setModal] = useState<{ course?: Course } | null>(null);
  const [busy, setBusy] = useState(false);
  const programName = new Map(programs.map((p) => [p.id, p.name]));

  async function toggleArchive(c: Course) {
    setBusy(true);
    try {
      await api.admin.updateCourse(c.id, { status: c.status === 'archived' ? 'active' : 'archived' });
      onChanged();
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Could not update course');
    } finally {
      setBusy(false);
    }
  }

  function meta(c: Course): string {
    return [
      c.programId ? programName.get(c.programId) : null,
      c.weeks ? `${c.weeks} weeks` : null,
      c.startDate ? `starts ${c.startDate.slice(0, 10)}` : null,
      c.slug,
    ]
      .filter(Boolean)
      .join('  ·  ');
  }

  return (
    <>
      <Card className="space-y-4 p-5">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-brand-navy">Courses</h3>
          <Button icon={Plus} onClick={() => setModal({})}>
            New course
          </Button>
        </div>
        <ul className="divide-y divide-slate-100">
          {courses.map((c) => (
            <li key={c.id} className="flex items-center gap-3 py-3">
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium text-brand-navy">{c.title}</div>
                <div className="truncate text-xs text-slate-400">{meta(c)}</div>
              </div>
              {c.status === 'archived' ? <Pill tone="slate">Archived</Pill> : <Pill tone="teal">Active</Pill>}
              <button
                className="btn-ghost p-2 text-slate-400 hover:text-brand-teal"
                title="Edit course"
                aria-label={`Edit ${c.title}`}
                onClick={() => setModal({ course: c })}
              >
                <PencilSimple size={18} />
              </button>
              <Button variant="secondary" onClick={() => toggleArchive(c)} disabled={busy}>
                {c.status === 'archived' ? 'Restore' : 'Archive'}
              </Button>
            </li>
          ))}
        </ul>
      </Card>

      {modal && (
        <CourseModal
          course={modal.course}
          programs={programs}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            onChanged();
          }}
        />
      )}
    </>
  );
}

// ── New / edit course modal ──────────────────────────────────────────────────
function CourseModal({
  course,
  programs,
  onClose,
  onSaved,
}: {
  course?: Course;
  programs: Program[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [title, setTitle] = useState(course?.title ?? '');
  const [programId, setProgramId] = useState(course?.programId ?? '');
  const [description, setDescription] = useState(course?.description ?? '');
  const [weeks, setWeeks] = useState(course?.weeks != null ? String(course.weeks) : '');
  const [startDate, setStartDate] = useState(course?.startDate ? course.startDate.slice(0, 10) : '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    const t = title.trim();
    if (!t) {
      setError('Course name is required.');
      return;
    }
    const body = {
      title: t,
      programId: programId || null,
      description: description.trim() || null,
      weeks: weeks.trim() ? Number(weeks) : null,
      startDate: startDate || null,
    };
    setSaving(true);
    setError(null);
    try {
      if (course) await api.admin.updateCourse(course.id, body);
      else await api.admin.createCourse(body);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save course');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={course ? 'Edit course' : 'New course'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" form="course-form" loading={saving}>
            Save
          </Button>
        </>
      }
    >
      <form id="course-form" onSubmit={save} className="space-y-4">
        <div>
          <label htmlFor="course-name" className="label-mono mb-1 block">
            Course name
          </label>
          <input
            id="course-name"
            className="input w-full"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Build and Secure AI Agent"
            autoFocus
            required
          />
        </div>

        <div>
          <label htmlFor="course-program" className="label-mono mb-1 block">
            Program <span className="font-normal normal-case text-slate-400">(optional)</span>
          </label>
          <select
            id="course-program"
            className="input w-full"
            value={programId}
            onChange={(e) => setProgramId(e.target.value)}
          >
            <option value="">— None —</option>
            {programs.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="course-desc" className="label-mono mb-1 block">
            Description <span className="font-normal normal-case text-slate-400">(optional)</span>
          </label>
          <textarea
            id="course-desc"
            className="input w-full"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Short summary students see."
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="course-weeks" className="label-mono mb-1 block">
              Duration (weeks)
            </label>
            <input
              id="course-weeks"
              type="number"
              min="1"
              className="input w-full"
              value={weeks}
              onChange={(e) => setWeeks(e.target.value)}
              placeholder="26"
            />
          </div>
          <div>
            <label htmlFor="course-start" className="label-mono mb-1 block">
              Start date
            </label>
            <input
              id="course-start"
              type="date"
              className="input w-full"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
        </div>

        {error && <p className="text-sm font-medium text-rose-600">{error}</p>}
      </form>
    </Modal>
  );
}
